import React, {
  PropsWithChildren,
  useState,
  createContext,
  Context,
  useMemo,
  useEffect,
} from 'react';
import useAvailableAssessmentsStore from 'graph/state/query-hooks/use-available-assessments';
import useUserStore from 'graph/state/query-hooks/use-user';
import {
  AssessmentType,
  BenchmarkAssessment,
  ProductCurriculum,
  UnitAssessment,
} from 'types/schema-generated-types';

const PRODUCT_CURRICULUM_ID_KEY = 'productCurriculumId';

interface IAssessmentContext {
  availableCurriculums: ProductCurriculum[];
  curriculumId: number | null;
  changeCurriculumId: (curriculumId: number) => void;
  isLoading: boolean;
}

export const AssessmentContext: Context<IAssessmentContext> = createContext<IAssessmentContext>({
  availableCurriculums: [],
  curriculumId: null,
  changeCurriculumId: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  isLoading: false,
});

export const AssessmentProvider: React.FunctionComponent<PropsWithChildren> = (props) => {
  const [{ me }, { getDefaultCurriculumCode, clearDefaultCurriculumCode }] = useUserStore();
  const [{ assessments, isLoading }] = useAvailableAssessmentsStore();
  const [curriculumId, setCurriculumId] = useState<number | null>(null);

  const [availableCurriculums, defaultCurriculumId] = useMemo(() => {
    const userCurriculums =
      me?.licenseUsage
        ?.filter((u) => u.assignedCount && u.productCurriculum)
        .map((u) => u.productCurriculum!) ?? [];

    const assessmentCurriculumIds = new Set<number>();
    for (const assessment of assessments) {
      if (assessment.type === AssessmentType.Benchmark) {
        const benchmarkAssessment = assessment as BenchmarkAssessment;
        benchmarkAssessment.details.forEach((detail) => {
          assessmentCurriculumIds.add(detail.productCurriculumId);
        });
      } else if (assessment.type === AssessmentType.Unit) {
        const unitAssessment = assessment as UnitAssessment;
        assessmentCurriculumIds.add(unitAssessment.unit.productCurriculumId);
      }
    }

    const curriculums = userCurriculums.filter((curriculum) =>
      assessmentCurriculumIds.has(curriculum?.id)
    );

    let defaultCurriculumId = curriculums.at(0)?.id;
    const defaultCurriculumCode = getDefaultCurriculumCode();
    const currentCurriculumId = sessionStorage.getItem(PRODUCT_CURRICULUM_ID_KEY);

    if (defaultCurriculumCode) {
      const curriculum = curriculums.find((c) => c.code === defaultCurriculumCode);
      if (curriculum?.id) defaultCurriculumId = curriculum.id;
    } else if (currentCurriculumId) {
      const curriculum = curriculums.find((c) => c.id === +currentCurriculumId);
      if (curriculum?.id) defaultCurriculumId = curriculum.id;
    }

    return [curriculums, defaultCurriculumId ?? null];
  }, [assessments, me?.licenseUsage, getDefaultCurriculumCode]);

  const changeCurriculumId: IAssessmentContext['changeCurriculumId'] = (newCurriculumId) => {
    if (newCurriculumId !== curriculumId) {
      sessionStorage.setItem(PRODUCT_CURRICULUM_ID_KEY, newCurriculumId.toString());
      setCurriculumId(newCurriculumId);
    }
  };

  useEffect(() => {
    // This should be the initializaiton of the curriculum.
    if (!curriculumId && defaultCurriculumId) {
      changeCurriculumId(defaultCurriculumId);
      clearDefaultCurriculumCode();
    }
  }, [curriculumId, defaultCurriculumId, changeCurriculumId, clearDefaultCurriculumCode]);

  return (
    <AssessmentContext.Provider
      value={{ availableCurriculums, isLoading, curriculumId, changeCurriculumId }}
    >
      {props.children}
    </AssessmentContext.Provider>
  );
};
export default AssessmentProvider;
